import { useContext, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { AppBar, Box, CssBaseline, IconButton, Stack, Toolbar, Typography, useTheme, Button, Container } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import MenuBottomNavigation from "@/components/MenuBottomNavigation";
import MenuSidebarNavigation from "@/components/MenuSidebarNavigation";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import { userSelector } from "@/features/auth/slice";
import { useGetCustomerByPhoneQuery } from "@/features/organizations/api/orgCustomersApi";
import { useGetLoyaltyAccountQuery, useFetchRedeemableContentQuery } from "@/api/loyaltyApi";
import LoyaltyAccountSummary from "@/components/loyalty/LoyaltyAccountSummary";
import LoyaltyAvailableRewards from "@/components/loyalty/LoyaltyAvailableRewards";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon, SmartPhone01Icon } from "@hugeicons/core-free-icons";
import useRoutes from "@/hooks/useRoutes";
import { openPhoneLoginDrawer } from "@/store/uiSlice";
import PhoneLoginDrawer from "@/components/loyalty/PhoneLoginDrawer";
import { sanitizePhone } from "@/utils/phones";

const LoyaltyCustomer = () => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [goToRoute] = useRoutes();
  const user = useAppSelector(userSelector);
  const { organization } = useContext(WebsiteContext);
  const isLoyaltyEnabled = organization?.settings?.loyaltyProgram?.isEnabled;

  const sanitizedPhone = useMemo(() => sanitizePhone(user?.phone ?? ""), [user?.phone]);

  // 1. Fetch customer by phone - Declarative approach
  const userByPhoneQuery = useGetCustomerByPhoneQuery(
    sanitizedPhone && organization?.id
      ? { organizationId: organization.id, phone: sanitizedPhone }
      : skipToken
  );

  const firstUserFound = useMemo(() => {
    const data = userByPhoneQuery.data;
    const results = Array.isArray(data) ? data : data ? [data] : [];
    return results[0];
  }, [userByPhoneQuery.data]);

  // 2. Fetch loyalty account once customer is identified - Declarative approach
  const loyaltyAccountQuery = useGetLoyaltyAccountQuery(
    firstUserFound?.id && organization?.id
      ? { organizationId: organization.id, uid: firstUserFound.id }
      : skipToken
  );

  // 3. Fetch rewards for the organization - Declarative approach
  const redeemableContentQuery = useFetchRedeemableContentQuery(
    organization?.id ? { organizationId: organization.id } : skipToken
  );

  if (!isLoyaltyEnabled) {
    return <Navigate to="/404" replace />;
  }

  const handleGoBack = () => {
    goToRoute("organizations.menu.main");
  };

  const handleOpenLogin = () => {
    dispatch(openPhoneLoginDrawer());
  };



  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {!isMobile && <MenuSidebarNavigation />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isMobile ? "100%" : "calc(100% - 280px)",
          ml: isMobile ? 0 : "280px",
          backgroundColor: "#F4F7FE",
          minHeight: "100vh",
          pb: 12,
          color: "text.primary",
        }}
      >
        <CssBaseline />
        {isMobile && (
          <AppBar elevation={1} color="default" position="sticky" sx={{ backgroundColor: "white" }}>
            <Toolbar>
              <Stack direction="row" flex={1} gap={2} alignItems="center">
                <IconButton onClick={handleGoBack}>
                  <HugeiconsIcon icon={ArrowLeft02Icon} color={theme.palette.primary.main} size={32} />
                </IconButton>
                <Typography variant="h4" component="div" sx={{ flexGrow: 1 }} color={"black"}>
                  Premios
                </Typography>
              </Stack>
            </Toolbar>
          </AppBar>
        )}

        <Container>
          <Stack spacing={4} sx={{ p: 3 }}>
            {user?.phone ? (
              <Stack spacing={5}>
                <LoyaltyAccountSummary
                  isLoading={userByPhoneQuery.isLoading || loyaltyAccountQuery.isLoading}
                  isSuccess={loyaltyAccountQuery.isSuccess}
                  currentPoints={loyaltyAccountQuery.data?.currentPoints ?? 0}
                  totalRedeemed={loyaltyAccountQuery.data?.totalRedeemed ?? 0}
                  totalEarned={loyaltyAccountQuery.data?.totalEarned ?? 0}
                />

                <LoyaltyAvailableRewards
                  isLoading={redeemableContentQuery.isLoading}
                  isSuccess={redeemableContentQuery.isSuccess}
                  rewards={redeemableContentQuery.data || []}
                  currentPoints={loyaltyAccountQuery.data?.currentPoints ?? 0}
                  onRedeem={() => { }}
                  readOnly={true}
                />
              </Stack>
            ) : (
              <Stack spacing={2} justifyContent={"center"} alignItems={"center"} sx={{ py: 10 }}>
                <Typography fontSize="4rem">🎁</Typography>
                <Typography variant="h2" color="text.primary">
                  Inicia sesión
                </Typography>
                <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ maxWidth: 300, mb: 2 }}>
                  Inicia sesión con tu número de teléfono para ver tus puntos y premios.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<HugeiconsIcon icon={SmartPhone01Icon} />}
                  onClick={handleOpenLogin}
                  sx={{ borderRadius: 4, px: 4, py: 1.5 }}
                >
                  Entrar con mi Teléfono
                </Button>
              </Stack>
            )}
          </Stack>
        </Container>

        <PhoneLoginDrawer />
        {isMobile && <MenuBottomNavigation />}
      </Box>
    </Box>
  );
};

export default LoyaltyCustomer;
